import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import MenuBookDetailModal from 'features/MenuBook/components/MenuBookDetailModal';
import menuBookService from 'features/SaleType/menubookService';
import { MenuBook } from 'features/SaleType/models';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TfiReload } from 'react-icons/tfi';
import { handleServiceError } from 'utils/axiosError';
import './style.scss';

interface FormSearchValueProps {
  name: string;
}

export interface SelectMenuBookModalReturnData {
  menuBookId: number;
  menuBookName: string;
  courseId: number | null;
  courseName?: string;
}

export interface SelectMenuBookModalProps {
  selectIncludeParent?: boolean;
  exceptMenuBookIds: number[];
  exceptCourseIds: number[];
  onClose?: () => void;
  onReflect?: (returnData: Array<SelectMenuBookModalReturnData>) => void;
}

function SelectMenuBookModal({
  selectIncludeParent = false,
  exceptMenuBookIds,
  exceptCourseIds,
  onClose,
  onReflect,
}: SelectMenuBookModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //Ref
  const formSearchRef = useRef<HTMLFormElement>(null);
  const tableDataRef = useRef<HTMLTableElement>(null);
  const buttonReflectRef = useRef<HTMLButtonElement>(null);
  const searchNameInputRef = useRef<HTMLInputElement>(null);

  //State
  const [showMenuBookDetailModal, setShowMenuBookDetailModal] = useState(false);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    name: '',
  });
  const [filter, setFilter] = useState<{ name: string }>({ name: '' });
  const [menuBookList, setMenuBookList] = useState<MenuBook[]>([]);

  async function loadMenuBookDataList() {
    try {
      const response = await menuBookService.getAll({
        name: filter.name,
        except_menu_book_ids: exceptMenuBookIds.join(','),
        except_course_ids: exceptCourseIds.join(','),
      });

      setMenuBookList(response.data);
    } catch (e) {
      handleServiceError(showGlobalModal, e as AxiosError);
    }
  }

  useEffect(() => {
    loadMenuBookDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function handleClose() {
    onClose && onClose();
  }

  function handleCollapsedFormSearch() {
    if (formSearchRef.current) {
      const formValue: FormSearchValueProps = {
        name: formSearchRef.current['search_name'].value.trim(),
      };
      setFormSearchValue(formValue);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (searchNameInputRef.current) {
      setFilter({ ...filter, name: searchNameInputRef.current.value.trim() });
    }
  }

  function handleSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-mbc-selection > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = true;
    });
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-mbc-selection > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });
  }

  function handleOpenMenuBookDetailModal() {
    setShowMenuBookDetailModal(true);
  }

  function handleCloseMenuBookDetailModal() {
    setShowMenuBookDetailModal(false);
  }

  function handleAddTimeFrameMenuBookCourse() {
    const checkedInputs = document.querySelectorAll<HTMLInputElement>(
      '.chk-mbc-selection > input[type="checkbox"]:checked'
    );
    const addData: Array<SelectMenuBookModalReturnData> = [];
    checkedInputs.forEach((input) => {
      const splitItems = input.value.split('-');
      const menuBookName = input.dataset.menubookname || '';
      const courseName = input.dataset.coursename;
      if (splitItems.length === 1) {
        addData.push({
          menuBookId: Number(splitItems[0]),
          courseId: null,
          menuBookName: menuBookName,
          courseName: courseName,
        });
      } else {
        if (addData.findIndex((item) => item.menuBookId === Number(splitItems[0]) && item.courseId === null) === -1) {
          selectIncludeParent &&
            addData.push({
              menuBookId: Number(splitItems[0]),
              courseId: null,
              menuBookName: menuBookName,
              courseName: courseName,
            });
        }
        addData.push({
          menuBookId: Number(splitItems[0]),
          courseId: Number(splitItems[1]),
          menuBookName: menuBookName,
          courseName: courseName,
        });
      }
    });

    if (addData.length > 0) {
      onReflect?.(addData);
      onClose?.();
    }
  }

  return (
    <Modal show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('SelectMenuBook.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form ref={formSearchRef} className="my-3" onSubmit={handleSubmit}>
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>{t('SelectMenuBook.search')}</Accordion.Header>
              <Accordion.Body onExited={handleCollapsedFormSearch}>
                <Row className="mb-3">
                  <Col sm={'auto'}>
                    <Form.Label htmlFor="txt-store-code" className="col-form-label">
                      {t('SelectMenuBook.menu_book_name')}：
                    </Form.Label>
                  </Col>
                  <Col sm={4}>
                    <Form.Control ref={searchNameInputRef} id="txt-name" name="search_name" />
                  </Col>
                  <Col sm={4}>
                    <Button type="submit">{t('SelectMenuBook.search')}</Button>
                  </Col>
                </Row>
              </Accordion.Body>
              <div className="accordion-body accordion-summary">
                <Row>
                  <Col sm={'auto'}>
                    <Form.Label className="col-form-label">{t('SelectMenuBook.menu_book_name')}：</Form.Label>
                    <Form.Label className="col-form-label">{formSearchValue.name || '-'}</Form.Label>
                  </Col>
                </Row>
              </div>
            </Accordion.Item>
          </Accordion>
        </form>
        <div className="text-end">
          <Button variant="link" onClick={handleOpenMenuBookDetailModal}>
            {t('SelectMenuBook.no_data_guide')}
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
          <div>
            <div className="d-inline-block me-4">
              <Button
                variant="link"
                className="px-0 text-decoration-none"
                onClick={handleSelectAll}
                style={{ minWidth: 'auto' }}
              >
                {t('SelectMenuBook.select_all')}
              </Button>
              <span>／</span>
              <Button
                variant="link"
                className="px-0 text-decoration-none"
                onClick={handleUnSelectAll}
                style={{ minWidth: 'auto' }}
              >
                {t('SelectMenuBook.unselect_all')}
              </Button>
            </div>
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              style={{ minWidth: 'auto' }}
              onClick={loadMenuBookDataList}
            >
              <span className="reload-icon d-inline-flex align-items-center justify-content-center">
                <TfiReload color="#fff" />
              </span>
              <span className="ms-1">{t('SelectMenuBook.reload')}</span>
            </Button>
          </div>
          <div>
            <Button ref={buttonReflectRef} className="btn--dark-green" onClick={handleAddTimeFrameMenuBookCourse}>
              {t('SelectMenuBook.addition')}
            </Button>
          </div>
        </div>
        <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '240px' }}>
          <Table ref={tableDataRef} hover>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>{t('SaleTypeListPage.selection')}</th>
                <th style={{ width: '45%' }}>{t('SelectMenuBook.menu_book_name')}</th>
                <th style={{ width: '45%' }}>{t('SelectMenuBook.course_name')}</th>
              </tr>
            </thead>
            <tbody>
              {menuBookList.map(function (menubook) {
                if (menubook.courses.length > 0) {
                  return menubook.courses.map(function (course) {
                    return (
                      <tr key={`menubook-${menubook.id}-course-${course.id}`} className="menu-book-row">
                        <td className="text-center">
                          <Form.Check
                            className="chk-mbc-selection custom-checkbox"
                            value={menubook.id + '-' + course.id}
                            data-menubookname={menubook.name}
                            data-coursename={course.name}
                          ></Form.Check>
                        </td>
                        <td className="text-center">{menubook.name}</td>
                        <td className="text-center">{course.name}</td>
                      </tr>
                    );
                  });
                }

                return (
                  <tr key={`menubook-${menubook.id}-course`} className="menu-book-row">
                    <td className="text-center">
                      <Form.Check
                        className="chk-mbc-selection"
                        value={menubook.id}
                        data-menubookname={menubook.name}
                      ></Form.Check>
                    </td>
                    <td className="text-center">{menubook.name}</td>
                    <td className="text-center"></td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {showMenuBookDetailModal &&
          ReactDOM.createPortal(<MenuBookDetailModal onClose={handleCloseMenuBookDetailModal} />, document.body)}
      </Modal.Body>
    </Modal>
  );
}

export default SelectMenuBookModal;
